import {
  type WabunkaExperienceReservationQuestion,
  type WabunkaReservationQuestion,
  WabunkaReservationQuestionType,
} from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_reservation_question';

import { convertNewlinesToBreaks } from '@/utils/stringUtils';


/**
 * proto enumからViewModel用の文字列に変換する
 */
const convertQuestionType = (type: WabunkaReservationQuestionType): string => {
  switch (type) {
    case WabunkaReservationQuestionType.WABUNKA_RESERVATION_QUESTION_TYPE_FREE_TEXT:
      return 'FreeText';
    case WabunkaReservationQuestionType.WABUNKA_RESERVATION_QUESTION_TYPE_RADIO:
      return 'Radio';
    case WabunkaReservationQuestionType.WABUNKA_RESERVATION_QUESTION_TYPE_CHECKBOX:
      return 'Checkbox';
    default:
      return 'FreeText';
  }
};

export interface WabunkaReservationQuestionViewModel {
  /** 予約質問マスタID */
  id: string;
  /** 質問内容 */
  content: string;
  /** プレースホルダー */
  placeHolder: string;
  /** 必須か */
  isRequired: boolean;
  /** シーケンスID */
  sequenceId: number;
  /** 質問タイプ (FreeText/Radio/Checkbox) */
  questionType: string;
  /** 選択肢の配列 */
  answerOptions: string[];
}
export interface WabunkaReservationQuestionAnswerViewModel extends WabunkaReservationQuestionViewModel {
  /** 回答 */
  answer: string;
}
/**
 * 予約質問ビューモデル のデフォルト値を返却する
 *
 * @returns {WabunkaReservationQuestionViewModel} - 予約質問ビューモデルのデフォルト値
 */
export const createDefaultWabunkaReservationQuestionViewModel = (): WabunkaReservationQuestionViewModel => ({
  id: '',
  content: '',
  placeHolder: '',
  isRequired: false,
  sequenceId: 1,
  questionType: 'FreeText',
  answerOptions: [],
});

/**
 * 予約時質問回答ビューモデル のデフォルト値を返却する
 *
 * @returns {WabunkaReservationQuestionAnswerViewModel} - 予約時質問回答ビューモデルのデフォルト値
 */
export const createDefaultWabunkaReservationQuestionAnswerViewModel = (): WabunkaReservationQuestionAnswerViewModel => ({
  id: '',
  content: '',
  placeHolder: '',
  isRequired: false,
  sequenceId: 1,
  questionType: 'FreeText',
  answerOptions: [],
  answer: '',
});

/**
 * APIレスポンスからビューモデルを作成する
 *
 * @param {WabunkaExperienceReservationQuestion[]} res - APIレスポンス
 * @returns {WabunkaExperienceEventInterpreterOptionViewModel[]} - ビューモデル
 */
export const buildWabunkaReservationQuestionListViewModel = (res: WabunkaExperienceReservationQuestion[]): WabunkaReservationQuestionViewModel[] =>
  res
    .filter((experienceReservationQuestion: WabunkaExperienceReservationQuestion) => experienceReservationQuestion.reservationQuestion !== undefined)
    .map((experienceReservationQuestion: WabunkaExperienceReservationQuestion): WabunkaReservationQuestionViewModel => {
      const reservationQuestion = experienceReservationQuestion.reservationQuestion as WabunkaReservationQuestion;
      return {
        id: reservationQuestion.id,
        content: reservationQuestion.content,
        placeHolder: reservationQuestion.placeHolder,
        isRequired: reservationQuestion.isRequired,
        sequenceId: experienceReservationQuestion.sequenceId,
        questionType: convertQuestionType(reservationQuestion.questionType),
        answerOptions: reservationQuestion.answerOptions ?? [],
      };
    })
    .sort((a, b) => a.sequenceId - b.sequenceId);

/**
 * 予約質問ビューモデルからビューモデルを作成する
 *
 * @param {WabunkaReservationQuestionViewModel[]} res - 予約質問ビューモデル
 * @returns {WabunkaReservationQuestionAnswerViewModel[]} - ビューモデル
 */
export const buildWabunkaReservationQuestionAnswerListViewModel = (res: WabunkaReservationQuestionViewModel[]): WabunkaReservationQuestionAnswerViewModel[] =>
  res.map((reservationQuestion: WabunkaReservationQuestionViewModel): WabunkaReservationQuestionAnswerViewModel => {
    const questionType = reservationQuestion.questionType ?? 'FreeText';
    const answerOptions = reservationQuestion.answerOptions ?? [];
    // radioの場合、デフォルトで最初の選択肢を選択する
    const defaultAnswer = questionType === 'Radio' && answerOptions.length > 0 ? JSON.stringify([answerOptions[0]]) : '';
    return {
      id: reservationQuestion.id,
      content: convertNewlinesToBreaks(reservationQuestion.content),
      placeHolder: reservationQuestion.placeHolder,
      isRequired: reservationQuestion.isRequired,
      sequenceId: reservationQuestion.sequenceId,
      questionType,
      answerOptions,
      answer: defaultAnswer,
    };
  });
